import CustomPagination from '@/platform/common/components/CustomPagination/CustomPagination';
import EmployeeCard from '@/platform/common/components/EmployeeCard/EmployeeCard';
import NoDataRow from '@/platform/common/components/NoDataRow/NoDataRow';
import PlaceholderValue from '@/platform/common/components/PlaceholderValue/PlaceholderValue';
import SearchBarFilter from '@/platform/common/components/SearchBarFilter/SearchBarFilter';
import { useFilters } from '@/platform/hooks/useFilters';
import { localDate } from '@/platform/lib/utils';
import {
    Button,
    Container,
    Dropdown,
    FilterBarDropdown,
    FilterBarToggle,
    Segment,
    SegmentContent,
    SegmentFooter,
    SegmentHeader,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/platform/ui';
import {
    ArrowDownTrayIcon,
    DocumentTextIcon,
    InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { Deferred, Link, useForm, usePage } from '@inertiajs/react';
import qs from 'qs';
import { useMemo, useState } from 'react';
import ExportModal from './components/ExportModal';
import Navigation from './components/Navigation';

const CustomerIndexView = () => {
    const { auth, employees, categories, offices, years } = usePage().props;
    const { filters, updateFilters, isSorted } = useFilters([
        'employees',
        'offices',
    ]);
    const [isEditing, setIsEditing] = useState(false);
    const { data, setData, reset, patch, processing, transform } = useForm([]);

    const officeOptions = useMemo(() => {
        if (!offices || offices?.length == 0) {
            return [];
        }

        return [
            ...offices,
            {
                id: 'without_office',
                name: 'Geen',
            },
        ];
    }, [offices]);

    const handleSubmit = () => {
        transform(data => ({ data: data }));

        patch(
            `/customers/${auth?.user?.selected_customer_id}/services/sroi/employees`,
            {
                preserveScroll: true,
                onSuccess: () => {
                    reset();
                    setIsEditing(false);
                },
            }
        );
    };

    const handleChange = (employeeId, value) => {
        setData([
            ...data.filter(datum => datum.employee_id !== employeeId),
            { employee_id: employeeId, category: value },
        ]);
    };

    let numberOfColumns = 5;

    if (auth?.permissions?.includes('export sroi')) {
        numberOfColumns += 1;
    }

    if (filters?.entity_ids?.length > 1) {
        numberOfColumns += 1;
    }

    if (filters?.office_ids?.length > 1) {
        numberOfColumns += 1;
    }

    return (
        <Container fluid>
            <Navigation />

            <Segment>
                <SegmentHeader
                    header={`Werknemers (${employees?.meta?.total || 0})`}
                    subheader='Sociale Terugkeer op Investering'
                >
                    {auth?.permissions?.includes('export sroi') && (
                        <>
                            <ExportModal />
                            <Button
                                as={Link}
                                target='_blank'
                                href={`/customers/${auth?.user?.selected_customer_id}/services/sroi/export-pso?${qs.stringify(
                                    {
                                        entity_ids: filters?.entity_ids,
                                        year: filters?.year,
                                    }
                                )}`}
                            >
                                <DocumentTextIcon className='size-4' />
                                Exporteren PSO
                            </Button>
                        </>
                    )}
                </SegmentHeader>
                <SegmentContent>
                    <SearchBarFilter only={['employees']}>
                        <FilterBarDropdown
                            multiple
                            selectAll
                            label={`Entiteiten (${filters?.entity_ids?.length || 0}/${auth?.customer?.entities?.length || 0})`}
                            options={auth.customer?.entities?.map(entity => ({
                                id: entity.id,
                                name: entity.name,
                            }))}
                            value={filters?.entity_ids || []}
                            onChange={value =>
                                updateFilters('entity_ids', value)
                            }
                        />
                        {officeOptions?.length > 0 && (
                            <FilterBarDropdown
                                multiple
                                selectAll
                                label={`Vestiging (${filters?.office_ids?.length || 0}/${officeOptions?.length || 0})`}
                                options={officeOptions || []}
                                value={filters?.office_ids || []}
                                onChange={value =>
                                    updateFilters('office_ids', value)
                                }
                            />
                        )}
                        <Deferred data='categories'>
                            <FilterBarDropdown
                                clearable
                                label={`Categorie (${categories?.find(cat => cat.id === filters.category)?.name || 'Alle'})`}
                                options={categories || []}
                                value={filters?.category || []}
                                onChange={value =>
                                    updateFilters('category', value)
                                }
                            />
                        </Deferred>
                        <Deferred data='years'>
                            <FilterBarDropdown
                                label={filters.year}
                                options={years || []}
                                value={filters?.year || []}
                                onChange={value => updateFilters('year', value)}
                            />
                        </Deferred>
                        <FilterBarToggle
                            label='Aflopend'
                            checked={!!filters?.expiring || false}
                            onChange={checked =>
                                updateFilters('expiring', checked ? 1 : 0)
                            }
                        />
                    </SearchBarFilter>

                    <div className='mb-4 flex items-center justify-end gap-2'>
                        {isEditing && (
                            <>
                                <Button
                                    variant='primary'
                                    onClick={handleSubmit}
                                    loading={processing}
                                >
                                    Opslaan
                                </Button>
                                <Button
                                    onClick={() => {
                                        reset();
                                        setIsEditing(false);
                                    }}
                                >
                                    Annuleren
                                </Button>
                            </>
                        )}
                        {!isEditing && (
                            <Button onClick={() => setIsEditing(true)}>
                                Bewerken
                            </Button>
                        )}
                    </div>

                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell>Medewerker</TableHeaderCell>
                                {filters?.entity_ids?.length > 1 && (
                                    <TableHeaderCell>Entiteit</TableHeaderCell>
                                )}
                                {filters?.office_ids?.length > 1 && (
                                    <TableHeaderCell>Vestiging</TableHeaderCell>
                                )}
                                <TableHeaderCell
                                    sorted={isSorted('employment_date')}
                                    onClick={() =>
                                        updateFilters('sort', 'employment_date')
                                    }
                                >
                                    Datum indienst
                                </TableHeaderCell>
                                <TableHeaderCell>
                                    Datum uitdienst
                                </TableHeaderCell>
                                <TableHeaderCell collapsing>
                                    Categorie
                                </TableHeaderCell>
                                <TableHeaderCell
                                    content=''
                                    sorted={isSorted('created_at')}
                                    onClick={() =>
                                        updateFilters('sort', 'created_at')
                                    }
                                >
                                    Aangemaakt op
                                </TableHeaderCell>
                                {auth?.permissions?.includes('export sroi') && (
                                    <TableHeaderCell textAlign='right'>
                                        Onderbouwing
                                    </TableHeaderCell>
                                )}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {employees?.meta?.total === 0 && (
                                <NoDataRow numberOfColumns={numberOfColumns} />
                            )}

                            {employees?.data?.map(employee => (
                                <TableRow key={employee.id}>
                                    <TableCell>
                                        <EmployeeCard employee={employee} />
                                    </TableCell>
                                    {filters?.entity_ids?.length > 1 && (
                                        <TableCell>
                                            {employee?.entity?.name}
                                        </TableCell>
                                    )}
                                    {filters?.office_ids?.length > 1 && (
                                        <TableCell>
                                            <PlaceholderValue
                                                isPresent={
                                                    employee?.office?.name
                                                }
                                                placeholder='Geen'
                                            >
                                                {employee?.office?.name}
                                            </PlaceholderValue>
                                        </TableCell>
                                    )}
                                    <TableCell>
                                        {localDate(employee.employment_date)}
                                        {employee.employments_count > 1 && (
                                            <Tooltip>
                                                <TooltipTrigger>
                                                    <InformationCircleIcon className='ml-2 size-4' />
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    {`Deze werknemer heeft ${employee.employments_count} dienstverbanden`}
                                                </TooltipContent>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <PlaceholderValue
                                            isPresent={
                                                employee.unemployment_date
                                            }
                                            placeholder='Onbepaald'
                                        >
                                            {localDate(
                                                employee.unemployment_date
                                            )}
                                        </PlaceholderValue>
                                    </TableCell>
                                    <TableCell>
                                        {!isEditing &&
                                            employee?.latest_entitlement
                                                ?.pso_category_label}
                                        {isEditing && (
                                            <Dropdown
                                                options={categories || []}
                                                value={
                                                    data?.find(
                                                        datum =>
                                                            datum.employee_id ===
                                                            employee.id
                                                    )?.category ||
                                                    employee?.latest_entitlement
                                                        ?.pso_category ||
                                                    ''
                                                }
                                                onChange={value =>
                                                    handleChange(
                                                        employee.id,
                                                        value
                                                    )
                                                }
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {localDate(employee?.created_at)}
                                    </TableCell>
                                    {auth?.permissions?.includes(
                                        'export sroi'
                                    ) && (
                                        <TableCell textAlign='right' collapsing>
                                            <Link
                                                target='_blank'
                                                rel='noopener noreferrer'
                                                href={`/customers/${auth?.user?.selected_customer_id}/services/sroi/${employee.id}/export`}
                                                className='inline-flex'
                                            >
                                                <ArrowDownTrayIcon className='size-5' />
                                            </Link>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </SegmentContent>

                <SegmentFooter>
                    <CustomPagination
                        meta={employees?.meta}
                        only={['employees']}
                    />
                </SegmentFooter>
            </Segment>
        </Container>
    );
};

export default CustomerIndexView;
